import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';

const ACTIVE_TIERS = ['Basic', 'Standard', 'Premium'];

/**
 * Fetches the current user's slice balance, tier, and next reset date.
 * Listens for the 'sliceBalanceChanged' DOM event so all instances
 * refresh automatically after a donation or grant.
 */
export function useSliceBalance() {
  const { user, isAuthenticated } = useAuth();
  const [balance, setBalance] = useState(null);
  const [tier, setTier] = useState(null);
  const [periodEnd, setPeriodEnd] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const fetchBalance = useCallback(async () => {
    if (!isAuthenticated || !user) {
      setBalance(null);
      setTier(null);
      setPeriodEnd(null);
      return;
    }
    setIsLoading(true);
    try {
      const { data } = await supabase
        .from('profiles')
        .select('slices, subscription_tier, subscription_current_period_end')
        .eq('id', user.id)
        .single();
      if (data) {
        setBalance(data.slices ?? 0);
        setTier(data.subscription_tier);
        setPeriodEnd(data.subscription_current_period_end);
      }
    } catch (err) {
      console.error('Failed to fetch slice balance:', err);
    } finally {
      setIsLoading(false);
    }
  }, [user, isAuthenticated]);

  useEffect(() => {
    fetchBalance();
  }, [fetchBalance]);

  // Re-fetch when another component triggers a balance change (e.g., after donation)
  useEffect(() => {
    window.addEventListener('sliceBalanceChanged', fetchBalance);
    return () => window.removeEventListener('sliceBalanceChanged', fetchBalance);
  }, [fetchBalance]);

  const isActiveSubscriber = ACTIVE_TIERS.includes(tier);

  return { balance, tier, periodEnd, isLoading, refetch: fetchBalance, isActiveSubscriber };
}
